import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import textsData from "./texts.json";

const StepCombined = ({
  formData,
  keywordInputs,
  handleInputChange,
  handleKeywordsChange,
  handleRemoveKeyword,
  handleKeywordFinalize,
  handleRubriqueChange,
  activeTab,
  handleTabChange,
  wordCounts,
  rubriques,
  rubriqueOptions,
  selectedRubriqueOptions,
  loadingRubriques,
  requiredField,
}) => {
  // === TEXTES MULTILINGUES DEPUIS JSON ===
  const TEXTS = textsData.languages;

  // === FONCTIONS UTILITAIRES ===

  // Fonction pour obtenir le texte selon la langue active
  const getText = (section) => {
    const currentLang = activeTab[section] || activeTab.title || "fr";
    return TEXTS[currentLang] || TEXTS.fr;
  };

  // Gestionnaire pour la saisie des mots-clés avec détection de virgule
  const handleKeywordsInputChange = (e) => {
    const value = e.target.value;
    const lang = activeTab.keywords;

    // Appeler la fonction qui gère la conversion automatique en tags
    handleKeywordsChange(lang, value);
  };

  // Gestionnaire pour la touche Entrée
  const handleKeywordsKeyDown = (e) => {
    const lang = activeTab.keywords;

    if (e.key === "Enter") {
      e.preventDefault();
      handleKeywordFinalize(lang);
    }
  };

  // Gestionnaire pour la perte de focus
  const handleKeywordsBlur = () => {
    const lang = activeTab.keywords;
    handleKeywordFinalize(lang);
  };

  // Fonction pour obtenir les mots-clés sous forme d'array
  const getKeywordsArray = () => {
    const keywords = formData.key_words_art[activeTab.keywords];
    return Array.isArray(keywords) ? keywords : [];
  };

  // Fonctions utilitaires pour l'interface
  const getLanguageFlag = (lang) => {
    if (lang === "fr") return "🇫🇷 FR";
    if (lang === "en") return "🇺🇸 EN";
    return "🇲🇬 MG";
  };

  const getTextareaBorderClass = (wordCount) => {
    if (wordCount > 300) {
      return "border-red-300 focus:border-red-500 focus:ring-red-500/20";
    }
    return "border-gray-300 focus:border-[#57A863] focus:ring-[#57A863]/20";
  };

  const getLanguageButtonClass = (isActive) => {
    if (isActive) {
      return "bg-[#57A863] text-white";
    }
    return "bg-gray-100 text-gray-600 hover:bg-gray-200";
  };

  const getOptionBackgroundColor = (state) => {
    if (state.isSelected) return "#57A863";
    if (state.isFocused) return "#f0fdf4";
    return "white";
  };

  const getOptionHoverColor = (isSelected) => {
    return isSelected ? "#57A863" : "#f0fdf4";
  };

  const formatDescription = (description) => {
    if (!description) return null;

    const truncatedDescription =
      description.length > 50
        ? `${description.substring(0, 50)}...`
        : description;

    return (
      <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
        {truncatedDescription}
      </div>
    );
  };

  // === STYLES PERSONNALISÉS POUR REACT-SELECT ===
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#57A863" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #57A863" : "none",
      "&:hover": {
        borderColor: "#57A863",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: getOptionBackgroundColor(state),
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: getOptionHoverColor(state.isSelected),
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#f0fdf4",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#166534",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#166534",
      "&:hover": {
        backgroundColor: "#57A863",
        color: "white",
      },
    }),
  };

  const formatOptionLabel = ({ label, description }) => (
    <div>
      <div style={{ fontWeight: "500" }}>{label}</div>
      {formatDescription(description)}
    </div>
  );

  // Obtenir l'array des mots-clés pour l'affichage
  const keywordsArray = getKeywordsArray();
  const currentKeywordInput = keywordInputs[activeTab.keywords] || "";

  return (
    <div className="step-content">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-700">
          {getText("title").pageTitle}
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          {getText("title").pageDescription}
        </p>
      </div>

      <div className="space-y-8">
        {/* 1. TITRE */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium text-gray-800">
                {getText("title").titleLabel} {requiredField}
              </h4>
              <p className="text-sm text-gray-500">
                {getText("title").titleDescription}
              </p>
            </div>
            <div className="flex space-x-1">
              {["fr", "en", "mg"].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${getLanguageButtonClass(
                    activeTab.title === lang
                  )}`}
                  onClick={() => handleTabChange("title", lang)}
                >
                  {getLanguageFlag(lang)}
                </button>
              ))}
            </div>
          </div>
          <input
            type="text"
            value={formData.title_art[activeTab.title] || ""}
            onChange={(e) =>
              handleInputChange("title_art", activeTab.title, e.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
            placeholder={getText("title").titlePlaceholder}
            required={activeTab.title === "fr"}
          />
        </div>

        {/* 2. RÉSUMÉ */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium text-gray-800">
                {getText("resume").resumeLabel} {requiredField}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({wordCounts[activeTab.resume] || 0}/300 mots)
                </span>
              </h4>
              <p className="text-sm text-gray-500">
                {getText("resume").resumeDescription}
              </p>
            </div>
            <div className="flex space-x-1">
              {["fr", "en", "mg"].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${getLanguageButtonClass(
                    activeTab.resume === lang
                  )}`}
                  onClick={() => handleTabChange("resume", lang)}
                >
                  {getLanguageFlag(lang)}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={formData.resume_art[activeTab.resume] || ""}
            onChange={(e) =>
              handleInputChange("resume_art", activeTab.resume, e.target.value)
            }
            rows={8}
            className={`w-full rounded-md border px-4 py-3 focus:outline-none focus:ring-2 ${getTextareaBorderClass(
              wordCounts[activeTab.resume]
            )}`}
            placeholder={getText("resume").resumePlaceholder}
            required={activeTab.resume === "fr"}
          />
          {wordCounts[activeTab.resume] > 300 && (
            <div className="mt-2 flex items-center text-sm text-red-600">
              <svg
                className="mr-1 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {getText("resume").resumeError}
            </div>
          )}
        </div>

        {/* 3. MOTS-CLÉS (SECTION ENTIÈREMENT CORRIGÉE) */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium text-gray-800">
                {getText("keywords").keywordsLabel} {requiredField}
              </h4>
              <p className="text-sm text-gray-500">
                {getText("keywords").keywordsDescription}
              </p>
            </div>
            <div className="flex space-x-1">
              {["fr", "en", "mg"].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${getLanguageButtonClass(
                    activeTab.keywords === lang
                  )}`}
                  onClick={() => handleTabChange("keywords", lang)}
                >
                  {getLanguageFlag(lang)}
                </button>
              ))}
            </div>
          </div>

          {/* CHAMP DE SAISIE CORRIGÉ */}
          <input
            type="text"
            value={currentKeywordInput}
            onChange={handleKeywordsInputChange}
            onKeyDown={handleKeywordsKeyDown}
            onBlur={handleKeywordsBlur}
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
            placeholder={getText("keywords").keywordsPlaceholder}
            required={activeTab.keywords === "fr" && keywordsArray.length === 0}
          />

          <p className="mt-2 text-xs text-gray-500">
            {getText("keywords").keywordsHelp}
          </p>

          {/* AFFICHAGE DES TAGS EXISTANTS */}
          {keywordsArray.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-medium text-gray-600">
                {keywordsArray.length} mot(s)-clé(s) :
              </div>
              <div className="flex flex-wrap gap-2">
                {keywordsArray.map((keyword, index) => (
                  <span
                    key={`keyword-${activeTab.keywords}-${index}-${keyword}`}
                    className="inline-flex items-center rounded-full border border-[#57A863]/20 bg-[#57A863]/10 px-3 py-1 text-sm font-medium text-[#57A863]"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveKeyword(activeTab.keywords, index)
                      }
                      className="ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#57A863]/20 text-[#57A863] hover:bg-[#57A863] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
                      title="Supprimer ce mot-clé"
                    >
                      <svg
                        className="h-2.5 w-2.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 4. CATÉGORIES */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              {getText("keywords").categoriesLabel} {requiredField}
            </h4>
            <p className="text-sm text-gray-500">
              {getText("keywords").categoriesDescription}
            </p>
          </div>
          <Select
            isMulti
            options={rubriqueOptions}
            value={selectedRubriqueOptions}
            onChange={handleRubriqueChange}
            formatOptionLabel={formatOptionLabel}
            styles={customSelectStyles}
            placeholder={getText("keywords").categoriesPlaceholder}
            noOptionsMessage={() => getText("keywords").categoriesNoOptions}
            loadingMessage={() => getText("keywords").categoriesLoading}
            isLoading={loadingRubriques}
            isDisabled={loadingRubriques}
            className="react-select-container"
            classNamePrefix="react-select"
          />
          {selectedRubriqueOptions.length > 0 && (
            <div className="mt-3 text-sm text-gray-500">
              ✅ {selectedRubriqueOptions.length}{" "}
              {getText("keywords").categoriesSelected}
            </div>
          )}

        </div>

        {/* 5. ZONE D'ÉTUDE */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              {getText("keywords").studyAreaLabel}
            </h4>
            <p className="text-sm text-gray-500">
              {getText("keywords").studyAreaDescription}
            </p>
          </div>
          <input
            type="text"
            value={formData.study_area_art || ""}
            onChange={(e) =>
              handleInputChange("study_area_art", null, e.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
            placeholder={getText("keywords").studyAreaPlaceholder}
          />
          <p className="mt-2 text-xs text-gray-500">
            {getText("keywords").studyAreaHelp}
          </p>
        </div>

        {/* 6. DOI */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              {getText("keywords").doiLabel}
            </h4>
            <p className="text-sm text-gray-500">
              {getText("keywords").doiDescription}
            </p>
          </div>
          <input
            type="text"
            value={formData.doi_art || ""}
            onChange={(e) => handleInputChange("doi_art", null, e.target.value)}
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
            placeholder={getText("keywords").doiPlaceholder}
          />
          <p className="mt-2 text-xs text-gray-500">
            {getText("keywords").doiHelp}
          </p>
          {!formData.doi_art && (
            <div className="mt-2 flex items-center text-sm text-blue-600">
              <svg
                className="mr-1 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {getText("keywords").doiAuto}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

StepCombined.propTypes = {
  formData: PropTypes.object.isRequired,
  keywordInputs: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleKeywordsChange: PropTypes.func.isRequired,
  handleRemoveKeyword: PropTypes.func.isRequired,
  handleKeywordFinalize: PropTypes.func.isRequired,
  handleRubriqueChange: PropTypes.func.isRequired,
  activeTab: PropTypes.object.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  wordCounts: PropTypes.object.isRequired,
  rubriques: PropTypes.array.isRequired,
  rubriqueOptions: PropTypes.array.isRequired,
  selectedRubriqueOptions: PropTypes.array.isRequired,
  loadingRubriques: PropTypes.bool.isRequired,
  requiredField: PropTypes.element.isRequired,
};

export default StepCombined;
